import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Stack } from '@rossum/ui/material';
import { organizationBranding } from '../../features/client-config/organization/branding';
import { useIsFeatureEnabled } from '../../features/feature-enablement/featureEnablement';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { currentTabSelector } from '../../redux/modules/router/selectors';
import { State } from '../../types/state';
import { defaultTab, tabToLink } from './navigationStructure';
import RossumLogo from './RossumLogo';

const organizationLoadedSelector = (state: State) =>
  !!safeOrganizationSelector(state)?.id;

export const hasCustomLogoSelector = (state: State) => {
  const organization = safeOrganizationSelector(state);

  if (!organization) {
    return false;
  }

  return !!(
    // there is an option to set top_left value to an empty string which should result in displaying PoweredBy
    (
      organizationBranding.customLogoImage(organization.uiSettings) === '' ||
      organizationBranding.customLogoImage(organization.uiSettings) ||
      organizationBranding.customBrandName(organization.uiSettings)
    )
  );
};

const Logo = () => {
  const organizationLoaded = useSelector(organizationLoadedSelector);
  const organization = useSelector(safeOrganizationSelector);
  const customLogo = organization
    ? organizationBranding.customLogoImage(organization.uiSettings)
    : null;
  const brandName = organization
    ? organizationBranding.customBrandName(organization.uiSettings)
    : null;
  const currentTab = useSelector(currentTabSelector);

  const { push } = useHistory();

  const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (currentTab?.name === defaultTab.name) return null;

    if (e.metaKey || e.ctrlKey) {
      return window.open(tabToLink(defaultTab), '_blank');
    }

    e.preventDefault();
    return push(tabToLink(defaultTab));
  };

  const isCustomFromSelector = useSelector(hasCustomLogoSelector);
  const isDemoMode = useIsFeatureEnabled('demo-mode');

  const isCustom = isDemoMode ? false : isCustomFromSelector;

  // there is an option to hide logo completely by setting top_left value to an empty string
  if (!organizationLoaded || customLogo === '') {
    return null;
  }

  return (
    <Stack
      onClick={onClick}
      sx={{ px: 2, cursor: 'pointer', flexShrink: 0 }}
      className="Logo"
    >
      {isCustom ? (
        <Stack
          direction="row"
          spacing={6}
          alignItems="center"
          sx={{
            '& img': {
              maxHeight: 36,
              width: '100%',
              height: 'auto',
            },
          }}
        >
          {customLogo && <img src={customLogo} alt="logo" />}
          {brandName && (
            <span style={{ textAlign: 'center', lineHeight: 1.1 }}>
              {brandName}
            </span>
          )}
        </Stack>
      ) : (
        <RossumLogo />
      )}
    </Stack>
  );
};

export default Logo;
